import React, { useState, useEffect } from 'react';
import { X, Package, DollarSign } from 'lucide-react';
import barrelReplenishmentService from '../../../services/barrelReplenishmentService';
import { barrelService } from '../../../services/barrelService';

const CreatePurchaseOrderModal = ({ onClose, onSuccess }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: '',
    barrel_type: 'noir_270l',
    quantity: 50,
    unit_price: 35.00,
    delivery_address: 'Entrepôt ADT, 23 rue de l\'essonne 91000 evry',
    payment_terms: 'Net 30',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [suppliersData, inventoryData] = await Promise.all([
        barrelReplenishmentService?.getSuppliers({ isActive: true }),
        barrelService?.getBarrelStatistics()
      ]);
      setSuppliers(suppliersData);
      setInventory(inventoryData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers?.find(s => s?.id === supplierId);
    if (supplier) {
      const price = formData?.barrel_type === 'noir_270l' 
        ? supplier?.noir_270l_price 
        : supplier?.bleu_220l_price;
      setFormData({
        ...formData,
        supplier_id: supplierId,
        unit_price: price || formData?.unit_price,
        payment_terms: supplier?.payment_terms || formData?.payment_terms
      });
    }
  };

  const handleBarrelTypeChange = (barrelType) => {
    const supplier = suppliers?.find(s => s?.id === formData?.supplier_id);
    const price = barrelType === 'noir_270l'
      ? (supplier?.noir_270l_price || 35.00)
      : (supplier?.bleu_220l_price || 40.00);
    setFormData({
      ...formData,
      barrel_type: barrelType,
      unit_price: price
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.supplier_id) {
      alert('Veuillez sélectionner un fournisseur');
      return;
    }

    try {
      setLoading(true);
      
      const totalAmount = formData?.quantity * formData?.unit_price;
      const expectedDeliveryDate = new Date();
      const supplier = suppliers?.find(s => s?.id === formData?.supplier_id);
      expectedDeliveryDate?.setDate(expectedDeliveryDate?.getDate() + (supplier?.delivery_time_days || 7));

      await barrelReplenishmentService?.createPurchaseOrder({
        ...formData,
        total_amount: totalAmount,
        expected_delivery_date: expectedDeliveryDate?.toISOString()?.split('T')?.[0],
        status: 'pending'
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error creating purchase order:', error);
      alert('Erreur lors de la création du bon de commande');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = formData?.quantity * formData?.unit_price;
  const selectedSupplier = suppliers?.find(s => s?.id === formData?.supplier_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Créer un Bon de Commande</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Supplier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fournisseur *
              </label>
              <select
                value={formData?.supplier_id}
                onChange={(e) => handleSupplierChange(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Sélectionner un fournisseur</option>
                {suppliers?.map((supplier) => (
                  <option key={supplier?.id} value={supplier?.id}>
                    {supplier?.name} - {supplier?.city} (Délai: {supplier?.delivery_time_days}j)
                  </option>
                ))}
              </select>
              {selectedSupplier && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Contact:</span>
                    <span className="font-medium text-gray-900">{selectedSupplier?.contact_person}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Téléphone:</span>
                    <span className="font-medium text-gray-900">{selectedSupplier?.phone}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Quantité min.:</span>
                    <span className="font-medium text-gray-900">{selectedSupplier?.minimum_order_quantity} unités</span>
                  </div>
                </div>
              )}
            </div>

            {/* Barrel Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de Barrique *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleBarrelTypeChange('noir_270l')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData?.barrel_type === 'noir_270l' ?'border-blue-600 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Package className="w-6 h-6 mx-auto mb-2 text-gray-700" />
                  <div className="font-semibold text-gray-900">Noir 270L</div>
                  <div className="text-sm text-gray-600">
                    {selectedSupplier?.noir_270l_price?.toFixed(2) || '35.00'}€/unité
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleBarrelTypeChange('bleu_220l')}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData?.barrel_type === 'bleu_220l' ?'border-blue-600 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Package className="w-6 h-6 mx-auto mb-2 text-blue-700" />
                  <div className="font-semibold text-gray-900">Bleu 220L</div>
                  <div className="text-sm text-gray-600">
                    {selectedSupplier?.bleu_220l_price?.toFixed(2) || '40.00'}€/unité
                  </div>
                </button>
              </div>
            </div>

            {/* Quantity and Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantité *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData?.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e?.target?.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix Unitaire (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData?.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e?.target?.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Total Amount Display */}
            <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-gray-900">Montant Total</span>
                </div>
                <span className="text-2xl font-bold text-green-600">
                  {totalAmount?.toFixed(2)}€
                </span>
              </div>
            </div>

            {/* Delivery Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse de Livraison *
              </label>
              <textarea
                value={formData?.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e?.target?.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Payment Terms */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conditions de Paiement
              </label>
              <input
                type="text"
                value={formData?.payment_terms}
                onChange={(e) => setFormData({ ...formData, payment_terms: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                rows={3}
                placeholder="Instructions spéciales, spécifications ADT, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 font-medium"
            >
              {loading ? 'Création...' : 'Créer le Bon de Commande'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePurchaseOrderModal;